/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone, FocusZoneDirection, IStackProps, ITextProps, IconButton, Image, Modal, Stack, Text } from '@fluentui/react';
import { getId } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { globalMessages } from 'src/core/utils/globalMessages';
import { ListBodies } from 'src/luis/verticals/introSlides/components/ListBodies';
import { ListItems } from 'src/luis/verticals/introSlides/components/ListItems';

export type IntroSlidesProps = {
    /**
     * Is the modal open.
     */
    isOpen: boolean;

    /**
     * A callback function on closing the modal.
     */
    onDismiss: () => void;
};

const messages = defineMessages({
    introSlidesHeader: {
        id: 'shared.introSlides.introSlidesHeader',
        defaultMessage: 'How to create an effective LUIS app'
    },
    carousel: {
        id: 'shared.introSlides.carousel',
        defaultMessage: 'Carousel'
    },
    next: {
        id: 'shared.introSlides.next',
        defaultMessage: 'Next'
    },
    previous: {
        id: 'shared.introSlides.previous',
        defaultMessage: 'Previous'
    },
    slide: {
        id: 'shared.introSlides.slide',
        defaultMessage: 'Slide'
    },
    slide1: {
        id: 'shared.introSlides.slide1',
        defaultMessage: 'Slide 1: Design your schema'
    },
    slide2: {
        id: 'shared.introSlides.slide2',
        defaultMessage: 'Slide 2: Build your model 1'
    },
    slide3: {
        id: 'shared.introSlides.slide3',
        defaultMessage: 'Slide 3: Build your model 2'
    },
    slide4: {
        id: 'shared.introSlides.slide4',
        defaultMessage: 'Slide 4: Improve your app 1'
    },
    slide5: {
        id: 'shared.introSlides.slide5',
        defaultMessage: 'Slide 5: Improve you app 2'
    },
    controlsAriaLabel: {
        id: 'shared.introSlides.controlsAriaLabel',
        defaultMessage: 'Slide controls'
    }
});

const { styleComponent, getClassName } = getStylistV2('IntroSlides');
const theme = getScopedTheme('IntroSlides');

const ContentContainer = styleComponent<IStackProps>(Stack)('ContentContainer', { height: '100%' });
const SlideContainer = styleComponent<IStackProps>(Stack)('SlideContainer', {
    paddingTop: theme.slidePaddingTop,
    height: theme.slideHeight
});
const IntroSlidesHeader = styleComponent<IStackProps>(Stack)('IntroSlidesHeader', {
    borderBottom: theme.borderSpecs,
    padding: theme.headerPadding
});
const Title = styleComponent<ITextProps>(Text)('Title', {
    fontWeight: theme.titleFontWeight as Property.FontWeight,
    color: theme.primaryFontColor
});

const navigationButtonStyle = getClassName('navigationButtonStyle', { backgroundColor: theme.navigationBtnBackgroundColor });

const modalScrollablePaneStyles = getClassName('modalScrollablePaneStyles', {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'unset'
});

const modalMainStyles = getClassName('modalMainStyles', {
    width: theme.modalWidth,
    height: theme.modalHeight,
    maxWidth: theme.modalMaxWidth,
    overflow: 'unset'
});

/**
 * Introductory slides to provide instructions on how
 * to create an effective LUIS app.
 */
export const IntroSlides = (props: IntroSlidesProps) => {
    const { isOpen, onDismiss } = props;
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();
    const [slideNumber, setSlideNumber] = React.useState<number>(0);
    const carouselId = getId();
    const logger = React.useRef(getLogger('IntroSlides'));

    React.useEffect(() => {
        if (isOpen) {
            logger.current.startEvent('IntroTutorialTeachingBubble');
        }
    }, []);

    const slideNavigatorCounter = [0, 1, 2, 3, 4];
    const imagesArray = [
        { name: 'designSchemaSlide', alt: localize(messages.slide1) },
        { name: 'buildModelSlide', alt: localize(messages.slide2) },
        { name: 'buildModelSlide2', alt: localize(messages.slide3) },
        { name: 'improveAppSlide', alt: localize(messages.slide4) },
        { name: 'improveAppSlide2', alt: localize(messages.slide5) }
    ];

    /**
     * Represents the number of the highlighted list item in the slide's index.
     */
    let listItemSelected;
    switch (slideNumber) {
        case 1:
        case 2:
            listItemSelected = 2;
            break;
        case 3:
        case 4:
            listItemSelected = 3;
            break;
        default:
            listItemSelected = 1;
    }

    /**
     * Increasing or decreasing the slide
     * number in a circular manner.
     */
    const modifySlideNumber = (sn: number) => setSlideNumber(sn > 4 ? sn - 5 : sn < 0 ? sn + 5 : sn);

    /**
     * A function to render an icon button with a
     * right/left arrow to navigate between slides.
     */
    const onRenderArrow = (direction: 'left' | 'right') => {
        const arrowStyles = {
            root: navigationButtonStyle,
            icon: { color: theme.selectedNavigationBtnColor, fontSize: theme.arrowBtnSize }
        };

        return (
            <LuisTooltip content={localize(direction === 'left' ? messages.previous : messages.next)}>
                <IconButton
                    role="button"
                    styles={arrowStyles}
                    data-automation-id={direction}
                    iconProps={{ iconName: direction === 'left' ? 'Back' : 'Forward' }}
                    ariaLabel={localize(direction === 'left' ? messages.previous : messages.next)}
                    onClick={() => modifySlideNumber(direction === 'left' ? slideNumber - 1 : slideNumber + 1)}
                />
            </LuisTooltip>
        );
    };

    const onDismissIntroSlide = () => {
        logger.current.endEvent('IntroTutorialTeachingBubble', false);
        onDismiss();
    };

    return (
        <Modal isOpen={isOpen} styles={{ main: modalMainStyles, scrollableContent: modalScrollablePaneStyles }} onDismiss={onDismiss}>
            <IntroSlidesHeader horizontal verticalAlign="center">
                <Stack.Item grow={1}>
                    <Title role="heading" aria-level={2} variant={isXLargeOrGreater ? 'medium' : 'small'}>
                        {localize(messages.introSlidesHeader)}
                    </Title>
                </Stack.Item>

                <LuisTooltip content={localize(globalMessages.cancel)}>
                    <IconButton
                        onClick={onDismissIntroSlide}
                        title={localize(globalMessages.cancel)}
                        data-automation-id="introSlidesCancelButton"
                        iconProps={{ iconName: 'Cancel', styles: { root: { color: theme.primaryFontColor } } }}
                    />
                </LuisTooltip>
            </IntroSlidesHeader>

            <ContentContainer id={carouselId} horizontalAlign="center" tokens={{ childrenGap: 15 }}>
                <SlideContainer
                    grow
                    aria-live="polite"
                    verticalAlign="center"
                    horizontalAlign="center"
                    horizontal={isXLargeOrGreater}
                    tokens={{ childrenGap: isXLargeOrGreater ? theme.slideChildrenGapXL : theme.slideChildrenGap }}>
                    {onRenderArrow('left')}

                    {isXLargeOrGreater && <ListItems currentSelection={listItemSelected} />}

                    <ListBodies currentSelection={slideNumber + 1} />

                    {onRenderArrow('right')}
                </SlideContainer>

                <FocusZone direction={FocusZoneDirection.horizontal} isCircularNavigation>
                    <Stack horizontal tokens={{ childrenGap: 30 }} role="listbox" aria-label={localize(messages.controlsAriaLabel)}>
                        {slideNavigatorCounter.map(index => (
                            <IconButton
                                role="option"
                                aria-controls={carouselId}
                                key={`carouselButton-${index}`}
                                className={navigationButtonStyle}
                                autoFocus={slideNumber === index}
                                onClick={() => setSlideNumber(index)}
                                aria-selected={slideNumber === index}
                                title={`${localize(messages.slide)} ${index + 1}`}
                                data-automation-id={`navigationButton ${index + 1}`}
                                ariaLabel={`${localize(messages.slide)} ${index + 1}`}
                                iconProps={{
                                    iconName: 'StatusCircleInner',
                                    style: {
                                        fontSize: theme.navigationBtnSize,
                                        color: slideNumber === index ? theme.selectedNavigationBtnColor : theme.navigationBtnColor
                                    }
                                }}
                            />
                        ))}
                    </Stack>
                </FocusZone>

                {isXLargeOrGreater && (
                    <Image
                        width="100%"
                        key={imagesArray[slideNumber].alt}
                        alt={imagesArray[slideNumber].alt}
                        src={getAssetsUrl(`assets/images/tutorialModal/${imagesArray[slideNumber].name}.gif`)}
                    />
                )}
            </ContentContainer>
        </Modal>
    );
};
